import { useState } from "react";
import { Link, Outlet } from "react-router-dom";
import {
  Phone,
  Menu,
  X,
  ChevronDown,
  FileText,
  Mail,
  Facebook,
  Instagram,
  Twitter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const services = [
  "Roof Repair & Shingle Installation",
  "Roof Replacement",
  "Kitchen Remodeling",
  "Bathroom Remodeling",
  "Flooring (Vinyl, LVP, Tile)",
  "Gutters",
  "Gutter Guards",
  "Concrete",
  "Shingles",
  "Window & Door Installation",
  "Porches & Decks",
  "Sunrooms",
  "Cabinet Installation",
  "Interior & Exterior Painting",
  "Full Home Remodels",
  "Tree Removal",
];

const serviceAreas = [
  {
    region: "Piedmont Triad",
    cities: [
      "Greensboro, NC",
      "High Point, NC",
      "Winston-Salem, NC",
      "Kernersville, NC",
      "Jamestown, NC",
      "Asheboro, NC",
      "Archdale, NC",
      "Trinity, NC",
      "Randleman, NC",
      "Thomasville, NC",
      "Lexington, NC",
    ],
  },
  {
    region: "Piedmont / Alamance",
    cities: ["Burlington, NC", "Mebane, NC", "Graham, NC", "Alamance, NC"],
  },
  {
    region: "Research Triangle",
    cities: [
      "Raleigh, NC",
      "Durham, NC",
      "Chapel Hill, NC",
      "Cary, NC",
      "Apex, NC",
      "Morrisville, NC",
      "Garner, NC",
      "Clayton, NC",
    ],
  },
  {
    region: "Chatham / Lee County",
    cities: ["Sanford, NC", "Pittsboro, NC"],
  },
];

export function Layout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans">
      <header className="sticky top-0 z-50 w-full bg-background shadow-sm">
        {/* Top Bar */}
        <div className="bg-primary text-black py-1.5 sm:py-2 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto flex flex-col sm:flex-row justify-center sm:justify-between items-center gap-1 sm:gap-0 text-[11px] sm:text-xs md:text-sm font-medium">
            <div className="flex items-center justify-center">
              <a
                href="tel:9198992094"
                className="flex items-center gap-1.5 hover:text-black/80 transition-colors"
              >
                <Phone className="h-3 md:h-4 w-3 md:w-4" /> 919-899-2094
              </a>
            </div>
            <div className="flex items-center justify-center text-center">
              <Link
                to="/two-day-refresh"
                className="text-black/90 font-semibold tracking-wide hover:text-black hover:underline transition-all"
              >
                Full interior painting for 2-bedroom homes. $950
              </Link>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="w-full px-4 sm:px-6 lg:px-12 border-b border-border/20 bg-background">
          <div className="flex h-20 lg:h-24 items-center justify-between">
            <Link
              to="/"
              className="flex items-center shrink-0 -ml-2 lg:-ml-6 lg:mr-4"
            >
              <img
                src="https://vibe.filesafe.space/1781836485235618884/attachments/86f992d7-6c85-4ed4-a894-bb7611e6666b.png"
                alt="Long Vista Remodeling and Exteriors LLC Logo serving Raleigh NC"
                className="h-12 md:h-16 lg:h-16 xl:h-20 scale-100 md:scale-110 object-contain ml-[-15px] mr-[-15px]"
                width="160"
                height="80"
              />
            </Link>

            <div className="hidden lg:flex flex-1 justify-center">
              <NavigationMenu delayDuration={0}>
                <NavigationMenuList className="gap-1 xl:gap-3">
                  <NavigationMenuItem>
                    <Link
                      to="/"
                      className="relative text-[14px] xl:text-[15px] font-medium text-foreground hover:text-primary transition-colors px-2 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100"
                    >
                      Home
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="relative h-auto px-2 py-2 text-[14px] xl:text-[15px] font-medium bg-transparent hover:bg-transparent hover:text-primary focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-primary after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100 data-[state=open]:after:origin-bottom-left data-[state=open]:after:scale-x-100">
                      Services
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                        {services.map((service) => (
                          <li key={service}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={`/services/${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-all duration-200 hover:bg-accent hover:text-accent-foreground hover:translate-x-1 focus:bg-accent focus:text-accent-foreground"
                              >
                                <div className="text-sm font-medium leading-none">
                                  {service}
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="relative h-auto px-2 py-2 text-[14px] xl:text-[15px] font-medium bg-transparent hover:bg-transparent hover:text-primary focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-primary after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100 data-[state=open]:after:origin-bottom-left data-[state=open]:after:scale-x-100">
                      Service Areas
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid w-[600px] grid-cols-2 gap-4 p-6 md:w-[800px] md:grid-cols-4">
                        {serviceAreas.map((area) => (
                          <div key={area.region} className="space-y-3">
                            <h4 className="font-semibold text-sm text-primary">
                              {area.region}
                            </h4>
                            <ul className="space-y-2">
                              {area.cities.map((city) => (
                                <li key={city}>
                                  <NavigationMenuLink asChild>
                                    <Link
                                      to={`/service-areas/${city
                                        .split(",")[0]
                                        .toLowerCase()
                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                      className="inline-block text-sm text-muted-foreground transition-all duration-200 hover:translate-x-1 hover:text-primary"
                                    >
                                      {city}
                                    </Link>
                                  </NavigationMenuLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link
                      to="/two-day-refresh"
                      className="relative text-[14px] xl:text-[15px] font-medium text-foreground hover:text-primary transition-colors px-2 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100"
                    >
                      The Two Day Refresh
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link
                      to="/gallery"
                      className="relative text-[14px] xl:text-[15px] font-medium text-foreground hover:text-primary transition-colors px-2 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100"
                    >
                      Gallery
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link
                      to="/#about"
                      className="relative text-[14px] xl:text-[15px] font-medium text-foreground hover:text-primary transition-colors px-2 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100"
                    >
                      About Us
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link
                      to="/blog"
                      className="relative text-[14px] xl:text-[15px] font-medium text-foreground hover:text-primary transition-colors px-2 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100"
                    >
                      Blog
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link
                      to="/contact"
                      className="relative text-[14px] xl:text-[15px] font-medium text-foreground hover:text-primary transition-colors px-2 py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100"
                    >
                      Contact Us
                    </Link>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div className="hidden lg:flex items-center shrink-0 gap-4 xl:gap-6">
              <div className="flex items-center gap-3 xl:gap-4">
                <a
                  href="https://www.houzz.com/pro/longvistaremodeling"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-primary transition-colors"
                  aria-label="Houzz"
                >
                  <svg
                    className="h-6 w-6 fill-current"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M1.27 0V7.32H8.5V0H1.27zm7.23 7.32v7.32h7.23V7.32H8.5zm-7.23 0v16.68H8.5V7.32H1.27zM15.5 14.64v9.36h7.23v-9.36H15.5z" />
                  </svg>
                </a>
                <a
                  href="https://nextdoor.com/page/long-vista-remodeling-exteriors-llc-greensboro-nc?utm_campaign=1782346604740&share_action_id=5cc0b02c-b218-4570-b4a7-cb04aae4619c"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-primary transition-colors"
                  aria-label="Nextdoor"
                >
                  <svg
                    className="h-6 w-6 fill-current"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12.008 0L.38 8.653A2.083 2.083 0 0 0 0 10.323v11.594A2.083 2.083 0 0 0 2.083 24h6.059v-9.59c0-1.874 1.258-2.484 2.373-2.484 1.115 0 2.373.61 2.373 2.484V24h9.029A2.083 2.083 0 0 0 24 21.917V10.323a2.083 2.083 0 0 0-.38-1.67L12.008 0z" />
                  </svg>
                </a>
              </div>
              <Button
                asChild
                className="bg-primary hover:bg-primary/90 text-white rounded-md font-bold px-6 xl:px-8 py-5 xl:py-6 text-[15px] xl:text-base"
              >
                <a href="tel:9198992094">Call Now</a>
              </Button>
            </div>

            <button
              className="lg:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-[100] bg-[#111111] text-white flex flex-col pt-6 pb-8 px-6 overflow-y-auto">
            <div className="flex justify-end mb-8 shrink-0">
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 text-white hover:text-gray-300"
                aria-label="Close menu"
              >
                <X className="h-8 w-8" />
              </button>
            </div>

            <div className="flex-1 flex flex-col items-center justify-start gap-8 my-auto py-8">
              <Link
                to="/"
                className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>

              <div className="w-full flex flex-col items-center">
                <button
                  onClick={() =>
                    setExpandedMenu(
                      expandedMenu === "services" ? null : "services",
                    )
                  }
                  className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors flex items-center gap-2"
                >
                  Services{" "}
                  <ChevronDown
                    className={`h-6 w-6 transition-transform ${expandedMenu === "services" ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedMenu === "services" && (
                  <div className="flex flex-col items-center gap-5 mt-8 mb-2">
                    {services.map((service) => (
                      <Link
                        key={service}
                        to={`/services/${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-base font-medium text-gray-300 hover:text-primary text-center"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {service}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="w-full flex flex-col items-center">
                <button
                  onClick={() =>
                    setExpandedMenu(expandedMenu === "areas" ? null : "areas")
                  }
                  className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors flex items-center gap-2"
                >
                  Service Areas{" "}
                  <ChevronDown
                    className={`h-6 w-6 transition-transform ${expandedMenu === "areas" ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedMenu === "areas" && (
                  <div className="flex flex-col items-center gap-8 mt-8 mb-2 w-full">
                    {serviceAreas.map((area) => (
                      <div
                        key={area.region}
                        className="flex flex-col items-center gap-4"
                      >
                        <div className="text-primary text-sm font-bold uppercase tracking-wider">
                          {area.region}
                        </div>
                        {area.cities.map((city) => (
                          <Link
                            key={city}
                            to={`/service-areas/${city
                              .split(",")[0]
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "-")}`}
                            className="text-base font-medium text-gray-300 hover:text-primary text-center"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {city}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/two-day-refresh"
                className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                The Two Day Refresh
              </Link>
              <Link
                to="/gallery"
                className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Gallery
              </Link>
              <Link
                to="/#about"
                className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              <Link
                to="/blog"
                className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/contact"
                className="text-2xl font-bold tracking-widest uppercase hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact Us
              </Link>
            </div>

            <div className="shrink-0 mt-8 flex flex-col gap-4">
              <Button
                onClick={() => {
                  setIsMenuOpen(false);
                  document.dispatchEvent(new CustomEvent("open-estimate"));
                }}
                className="w-full bg-primary hover:bg-primary/90 text-white text-lg font-bold py-6 rounded-lg uppercase tracking-wide flex items-center justify-center gap-2"
              >
                Get a Free Quote <span className="text-xl">→</span>
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full bg-transparent text-white border-2 border-white hover:bg-white/10 hover:text-white text-lg font-bold py-6 rounded-lg uppercase tracking-wide"
              >
                <a
                  href="tel:9198992094"
                  className="flex items-center justify-center gap-2"
                >
                  <Phone className="h-5 w-5 fill-current" /> Call Now
                </a>
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-background border-t border-border/40 pt-16 pb-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12 mb-12">
            <div className="lg:col-span-1">
              <img
                src="https://vibe.filesafe.space/1781836485235618884/attachments/86f992d7-6c85-4ed4-a894-bb7611e6666b.png"
                alt="Long Vista Remodeling and Exteriors LLC Logo serving Raleigh NC"
                className="h-16 object-contain mb-6"
                width="160"
                height="64"
              />
              <p className="text-muted-foreground mb-6 text-sm">
                One Contractor. Every Job. Roofing, kitchens, bathrooms,
                flooring, windows, decks and more.
              </p>
              <div className="space-y-2">
                <a
                  href="tel:9198992094"
                  className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium"
                >
                  <Phone className="h-4 w-4" /> 919-899-2094
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-foreground">
                Quick Links
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/two-day-refresh"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    The Two Day Refresh
                  </Link>
                </li>
                <li>
                  <Link
                    to="/gallery"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Gallery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#about"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Blog
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-foreground">
                Resources
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/bathroom-remodel-cost-raleigh"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Bathroom Cost Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/kitchen-remodel-cost-nc"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Kitchen Cost Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/remodel-timeline"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Remodel Timelines
                  </Link>
                </li>
                <li>
                  <Link
                    to="/nc-remodel-permits"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    NC Permit Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    FAQ
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4 text-foreground">
                Services
              </h3>
              <ul className="space-y-2">
                {services.map((service) => (
                  <li key={service}>
                    <Link
                      to={`/services/${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {service}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2">
              <h3 className="font-bold text-lg mb-4 text-foreground">
                Service Areas
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {serviceAreas.map((area) => (
                  <div key={area.region}>
                    <h4 className="text-sm font-semibold text-foreground mb-2">
                      {area.region}
                    </h4>
                    <ul className="space-y-1">
                      {area.cities.map((city) => (
                        <li key={city}>
                          <Link
                            to={`/service-areas/${city
                              .split(",")[0]
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "-")}`}
                            className="text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            {city}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-border/40 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6">
              <p className="text-muted-foreground text-sm text-center md:text-left">
                © {new Date().getFullYear()} Long Vista Remodeling & Exteriors
                LLC. All rights reserved.
              </p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <Link
                  to="/privacy"
                  className="hover:text-primary transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="hover:text-primary transition-colors"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
